import React from 'react'

import { FormikProps } from 'formik'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../components/Panel'
import { usePanelContext } from '../../../../contexts/panel'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import i18n from '../../../../utils/i18n'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { DefaultConfig } from '../../../ui-config/data'
import { DataForConfirmationDto } from '../../data'
import { FormFieldName } from './DataForConfirmation.form'

type Props = {
  data: DataForConfirmationDto
  config: DefaultConfig
  formik: FormikProps<DataForConfirmationDto>
  isFormFieldValid: (name: FormFieldName) => boolean
  movedComponentIfErrorMsgEnable: (msgIdText: any) => JSX.Element
  isLoading: boolean
}

export const DataForConfirmationView: React.FC<Props> = ({
  data,
  config,
  formik,
  isFormFieldValid,
  movedComponentIfErrorMsgEnable,
  isLoading,
}) => {
  const { language } = i18n

  const panelId = 'delivery-call-offs-data-for-confirmation'
  const { isEdit } = usePanelContext()

  // ************************************************************
  // components
  // ************************************************************
  // responsePeriod
  const responsePeriod: Date[] = [
    new Date(Date.parse(data?.responsePeriod?.Start!)),
    new Date(Date.parse(data?.responsePeriod?.End!)),
  ]

  const responsePeriodLabel = (
    <label className="text-sm" htmlFor="responsePeriod"></label>
  )

  const responsePeriodComponent = (
    <Calendar
      id="responsePeriod"
      value={responsePeriod}
      selectionMode="range"
      className="w-full"
      readOnlyInput
      showIcon
      locale={language}
      disabled
    />
  )
  const responsePeriodErrorMsg =
    movedComponentIfErrorMsgEnable('responsePeriod')

  const responsePeriodWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.dataForConfirmation}
      component={responsePeriodComponent}
      label={responsePeriodLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={responsePeriodErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationId
  const confirmationIdLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('confirmationId'),
      })}
      htmlFor="confirmationId"
    ></label>
  )

  const confirmationIdComponent = (
    <InputText
      id="confirmationId"
      value={formik.values.confirmationId}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('confirmationId'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationIdErrorMsg =
    movedComponentIfErrorMsgEnable('confirmationId')

  const confirmationIdWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.dataForConfirmation}
      component={confirmationIdComponent}
      label={confirmationIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationIdErrorMsg}
      isLoading={isLoading}
    />
  )

  // department
  const departmentLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('department'),
      })}
      htmlFor="department"
    ></label>
  )

  const departmentComponent = (
    <InputText
      id="department"
      value={formik.values.department}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('department'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const departmentErrorMsg = movedComponentIfErrorMsgEnable('department')

  const departmentWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.dataForConfirmation}
      component={departmentComponent}
      label={departmentLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={departmentErrorMsg}
      isLoading={isLoading}
    />
  )

  // contactName
  const contactNameLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('contactName'),
      })}
      htmlFor="contactName"
    ></label>
  )

  const contactNameComponent = (
    <InputText
      id="contactName"
      value={formik.values.contactName}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('contactName'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const contactNameErrorMsg = movedComponentIfErrorMsgEnable('contactName')

  const contactNameWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.dataForConfirmation}
      component={contactNameComponent}
      label={contactNameLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={contactNameErrorMsg}
      isLoading={isLoading}
    />
  )

  // contactPhone
  const contactPhoneLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('contactPhone'),
      })}
      htmlFor="contactPhone"
    ></label>
  )

  const contactPhoneComponent = (
    <InputText
      id="contactPhone"
      value={formik.values.contactPhone}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('contactPhone'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const contactPhoneErrorMsg = movedComponentIfErrorMsgEnable('contactPhone')

  const contactPhoneWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.dataForConfirmation}
      component={contactPhoneComponent}
      label={contactPhoneLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={contactPhoneErrorMsg}
      isLoading={isLoading}
    />
  )

  // contactMail
  const contactMailLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': isFormFieldValid('contactMail'),
      })}
      htmlFor="contactMail"
    ></label>
  )

  const contactMailComponent = (
    <InputText
      id="contactMail"
      value={formik.values.contactMail}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('contactMail'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const contactMailErrorMsg = movedComponentIfErrorMsgEnable('contactMail')

  const contactMailWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecast.dataForConfirmation}
      component={contactMailComponent}
      label={contactMailLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={contactMailErrorMsg}
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      formik={formik}
      header={getConfigTranslation(
        config.deliveryForecast.dataForConfirmation.title,
      )}
      id={panelId}
      visible={config.deliveryForecast.dataForConfirmation.enabled}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="formgrid grid">
          <div className="col-12">
            <div className="formgrid grid">
              {/* responsePeriod */}
              {responsePeriodWrapper}

              {/* confirmationId */}
              {confirmationIdWrapper}
            </div>
          </div>

          {/* department */}
          {departmentWrapper}

          {/* contactName */}
          {contactNameWrapper}

          {/* contactPhone */}
          {contactPhoneWrapper}

          {/* contactMail */}
          {contactMailWrapper}
        </div>
      </form>
    </PanelContainer>
  )
}
